import { sqWorkbenchStore } from '@/core/core.stores';
import { invalidateTemplateLists } from '@/tools/models/template.queries';
import { TEMPLATE_TYPES } from '@/tools/models/template.types';
import { fetchTemplate } from '@/tools/models/template.utilities';
import { isModel } from '@/utilities/utilities';
import { fetchScalingTables, fetchTemplates } from '@/workbook/workbook.actions';
import { recordStateRead } from '@/worksteps/stateTelemetry.utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';
import type { QueryClient } from '@tanstack/react-query';
import type { DehydratedState } from '@/services/stateSynchronizer.service';
import type { TItemProperties } from '@/tools/itemProperties/hooks/useProperties';
import type { CurrentUser } from '@/workbench/workbench.store';
import type { StateOrigin } from '@/worksteps/stateTelemetry.utilities';
import type { IdentityPreviewV1 } from '@seeqdev/api/model/IdentityPreviewV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';

export const WORKBOOK_SCHEMA_VERSION = 1;
export const WORKBOOK_STATE_PROPERTY = 'workbookState';

/** A workbook's saved state and, when it was read from a saved blob, the schema-version range it was read across. */
export type SavedWorkbookState = { state: DehydratedState; origin: StateOrigin | undefined };

/**
 * Unpacks a saved workbookState property. This is the read boundary for workbook state, so the read is recorded
 * here for the state telemetry. There is no workbook upgrader and the saved wrapper's version has always been 1;
 * the fallback to v1 covers blobs whose wrapper lacks the field.
 */
export function parseSavedWorkbookState(json: string): SavedWorkbookState {
  const { version, state } = JSON.parse(json);
  const origin = { fromVersion: version || 1, toVersion: WORKBOOK_SCHEMA_VERSION };
  recordStateRead('workbook', origin.fromVersion, origin.toVersion);
  return { state, origin };
}

/**
 * Get the current user's workbook state. If there is no state, it returns an empty object.
 *
 * @returns A promise that will resolve with state for the current user's workbook.
 */
export function getWorkbook(id: string): Promise<SavedWorkbookState | undefined> {
  return sqItemsApi
    .getProperty({ id, propertyName: WORKBOOK_STATE_PROPERTY })
    .then(({ data }) => {
      // First-time user that has no workbook state
      return data === undefined
        ? { state: {} as DehydratedState, origin: undefined }
        : parseSavedWorkbookState(data.value as string);
    })
    .catch(() => {
      // Our frontend code now adds default workbook state when a workbook is created via workbench, so
      // most of the time this catch will not be hit. We still need the catch though for previously-created
      // workbooks and workbooks created through the REST API.
      return undefined;
    });
}

/**
 * Set the workbook state for the current user
 *
 * @param id
 * @param state - The workbook state object
 * @returns {Promise} A promise that will resolve when the current user's workbook state has been set.
 */
export function setWorkBook(id: string, state: any) {
  const workbookData = JSON.stringify({
    version: WORKBOOK_SCHEMA_VERSION,
    state,
  });
  return sqItemsApi
    .setProperty({ value: workbookData }, { id, propertyName: WORKBOOK_STATE_PROPERTY })
    .then(({ data }) => data);
}

export const fetchAndAddTableTypePropertyToItems = async (items: ItemSearchPreviewV1[]) => {
  return await Promise.all(
    items.map(async (item) => {
      if (item.type === SeeqNames.Types.TableDefinition) {
        const tableTypeResponse = await sqItemsApi.getProperty({
          id: item.id,
          propertyName: SeeqNames.Properties.TableType,
        });
        const tableType = tableTypeResponse.data.value;
        if (item.includedProperties && tableType) {
          item.includedProperties[SeeqNames.Properties.TableType] = {
            name: SeeqNames.Properties.TableType,
            value: tableType,
          };
        }
      }
      return item;
    }),
  );
};

export const refetchNonTrendableItems = async (item: TItemProperties, queryClient: QueryClient) => {
  if (item.type === SeeqNames.Types.TableDefinition) {
    await fetchScalingTables([sqWorkbenchStore.stateParams.workbookId]);
  } else if (isModel(item)) {
    await fetchTemplate(item.id, TEMPLATE_TYPES.TOOL);
    invalidateTemplateLists(queryClient);
    await fetchTemplates([sqWorkbenchStore.stateParams.workbookId]);
  }
};

export const determineIsOwner = (owner: string | IdentityPreviewV1, currentUser: CurrentUser) =>
  currentUser.id === (typeof owner === 'string' ? owner : owner?.id);
